import { useQuery, useApolloClient, useMutation, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { FormattedTime } from '@10xLabs/react-web/components/FormattedTime'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
// @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
import { PlusIcon } from '@10xLabs/react-web/components/Icons/Plus'
import { StyledLink } from 'components/v2/Link'
import { Dropdown } from 'components/v2/Dropdown'
import List from 'components/v2/List'
import { Switch } from 'components/v2/Switch'
import { LayoutContext } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { IsActive } from 'components/Chip'
import { STATUS } from 'features/Services/components/Status/constants'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { confirmationModalitiesMessages } from 'features/Services/components/ConfirmationModalities/messages'
import { GET_SERVICES } from 'features/Services/queries-legacy'
import { resolvers as routesResolvers } from 'resolvers/routes'
import { resolvers as servicesResolvers, getConfirmationModalitiesAsArray, getDayLabel } from 'resolvers/services'
import { useLogin } from 'features/Login/hooks'
import { ServicesFilters } from './Filters'
import { ServicesPagination } from './Pagination'
import { parseDate } from 'utils/date'
import { canRead, canWrite } from 'features/Users/utils'
import { businessClientsToString } from 'features/Trips/utils'
import { GTRouteServiceBusinessSite, GTService, GTTRoute } from 'gql/types.generated'
import { getPlanningsByService, mapServiceQueryToEditForm } from 'models/services'
import { getUserFullName } from 'features/UserAccount/utils'

const LIMIT = 30
const FIRST_PAGE = 1

const ServicesFiltersWrapper = styled.div`
  margin-bottom: 10px;
`

const Footer = styled.div<TSFixMe>`
  margin: 20px 0 0;
`

const AddAction = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #5d59d7;
  font-size: 16px;
  text-decoration: none;

  svg {
    margin-right: 7px;
    fill: #5d59d7;
    width: 18px;
    height: 18px;
  }
`

const ACTIVATE_SERVICE = gql`
  mutation ActivateService($serviceId: ID!) {
    activateService(input: { ID: $serviceId }) {
      id
      isActive
    }
  }
`

const DEACTIVATE_SERVICE = gql`
  mutation DeactivateService($serviceId: ID!) {
    deactivateService(input: { ID: $serviceId }) {
      id
      isActive
    }
  }
`

const UPDATE_SERVICE_IS_ACTIVE = gql`
  mutation UpdateServiceIsActive($serviceId: ID!, $isActive: Boolean!) {
    updateServiceIsActive(serviceId: $serviceId, isActive: $isActive) @client
  }
`

const ToggleServiceActive = ({ service }: TSFixMe): TSFixMe => {
  const client = useApolloClient()
  client.addResolvers(servicesResolvers)

  const [updateServiceIsActive] = useMutation(UPDATE_SERVICE_IS_ACTIVE)
  const [activateService] = useMutation(ACTIVATE_SERVICE, {
    onError() {
      updateServiceIsActive({
        variables: {
          serviceId: service.id,
          isActive: false
        }
      })
    }
  })
  const [deactivateService] = useMutation(DEACTIVATE_SERVICE, {
    onError() {
      updateServiceIsActive({
        variables: {
          serviceId: service.id,
          isActive: true
        }
      })
    }
  })

  const toggleActive = (): TSFixMe => {
    if (service.isActive) {
      deactivateService({
        variables: {
          serviceId: service.id
        }
      })
      updateServiceIsActive({
        variables: {
          serviceId: service.id,
          isActive: false
        }
      })
    } else {
      activateService({
        variables: {
          serviceId: service.id
        }
      })
      updateServiceIsActive({
        variables: {
          serviceId: service.id,
          isActive: true
        }
      })
    }
  }

  return <Switch value={service.isActive} onChange={toggleActive} small />
}

const ServicesTable = ({ services }: TSFixMe): TSFixMe => {
  const intl = useIntl()
  const login = useLogin()

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            <Th>
              <FormattedMessage id="services.list.columns.number" defaultMessage="Code" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.departure" defaultMessage="Departure" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.dow" defaultMessage="DOW" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.startingAt" defaultMessage="Starting At" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.endingAt" defaultMessage="Ending At" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.vehicle" defaultMessage="Vehicle" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.modalities" defaultMessage="Modalities" />
            </Th>
            <Th>
              <FormattedMessage id="routes.services.list.clients" defaultMessage="Client - Site" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.line" defaultMessage="Line" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {services.map((service: GTService): ReactElement => {
            const confirmationModalities = getConfirmationModalitiesAsArray(service.confirmationModalities)
            const serviceForm = mapServiceQueryToEditForm(service)
            const plannings = getPlanningsByService(serviceForm.services[0])
            const lastPlanning = plannings.at(-1)
            const originDepartureTime = lastPlanning?.itineraries.at(0)?.schedule.at(0)?.departure?.time || ''
            const operationDays = lastPlanning?.itineraries.at(0)?.operationDays
            const { vehicle, driver } = service?.vehiclesSchedule.at(-1) || {}

            return (
              <Tr key={service.id}>
                <Td>
                  <StyledLink as={NavLink} to={`/services/${service.id}`}>
                    {service.code}
                  </StyledLink>
                </Td>
                <Td>
                  <FormattedTime time={originDepartureTime} noIcon />
                </Td>
                <Td>{operationDays}</Td>
                <Td>
                  <FormattedDate
                    intl={intl}
                    value={parseDate(service.startAt)}
                    month="2-digit"
                    day="2-digit"
                    year="numeric"
                  />
                </Td>
                <Td>
                  {service.endAt ? (
                    <FormattedDate
                      intl={intl}
                      value={parseDate(service.endAt)}
                      month="2-digit"
                      day="2-digit"
                      year="numeric"
                    />
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>
                  {vehicle ? (
                    <>
                      {vehicle.externalID} - {vehicle.name}{' '}
                      {driver?.userAccount ? `(${getUserFullName(driver.userAccount)})` : ''}
                    </>
                  ) : (
                    '-'
                  )}
                </Td>
                <Td>
                  {confirmationModalities.length > 0 ? (
                    <Dropdown toggle={<StyledLink>{confirmationModalities.length}</StyledLink>} noPadding>
                      <List.Container>
                        {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
                        {confirmationModalities.map((modality, index): TSFixMe => {
                          return (
                            <List.Item key={index}>
                              {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
                              {confirmationModalitiesMessages[modality]
                                ? // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
                                  intl.formatMessage(confirmationModalitiesMessages[modality])
                                : modality}
                            </List.Item>
                          )
                        })}
                      </List.Container>
                    </Dropdown>
                  ) : (
                    0
                  )}
                </Td>
                <Td>{businessClientsToString((service.businessClients || []) as GTRouteServiceBusinessSite[])}</Td>
                <Td>{service.companyLine.name}</Td>
                <Td>
                  {canWrite(FEATURES.SERVICE, login.accessToken.policies) ? (
                    <ToggleServiceActive service={service} />
                  ) : (
                    <IsActive isActive={service.isActive} />
                  )}
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}

const EMPTY_FILTERS = {
  status: '',
  vehicle: { id: '', name: '' },
  modalities: [],
  dow: []
}

interface IServicesListProps {
  route: GTTRoute
}

export const ServicesList = ({ route }: IServicesListProps): ReactElement => {
  let content
  const client = useApolloClient()
  client.addResolvers(routesResolvers)
  const login = useLogin()
  const { layout } = useContext(LayoutContext)

  const [page, setPage] = useState(FIRST_PAGE)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
  } = useQuery(GET_SERVICES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        size: LIMIT,
        page: page - 1
      },
      input: {
        routeID: route.id,
        isActive: !isEmpty(filters.status) ? filters.status === STATUS.ACTIVE : undefined,
        vehicleID: !isEmpty(filters.vehicle.id) ? filters.vehicle.id : undefined,
        confirmationModalities: !isEmpty(filters.modalities)
          ? filters.modalities.map((modality: string) => modality.toUpperCase())
          : undefined,
        dow: !isEmpty(filters.dow) ? filters.dow.map(getDayLabel) : undefined
      }
    }
  })

  useEffect((): TSFixMe => {
    setFilters(EMPTY_FILTERS)
    setPage(FIRST_PAGE)
  }, [route && route.id])

  useEffect((): TSFixMe => {
    setPage(FIRST_PAGE)
  }, [filters])

  useEffect((): TSFixMe => {
    const servicesListEl = document.getElementById('services-list')
    if (servicesListEl && layout.appContentEl?.current) {
      layout.appContentEl.current.scrollTop = servicesListEl.offsetTop
    }
  }, [page])

  if (!canRead(FEATURES.SERVICE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SERVICE} />
  }

  const handleFiltersChange = (values: TSFixMe): TSFixMe => {
    setFilters({
      status: values.status || EMPTY_FILTERS.status,
      vehicle: {
        id: values.vehicle && values.vehicle.id,
        name: values.vehicle && values.vehicle.name
      },
      modalities: values.modalities || EMPTY_FILTERS.modalities,
      dow: values.dow || EMPTY_FILTERS.dow
    })
  }

  const services = data?.servicesWithPagination?.items || []

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="services.error.description"
            defaultMessage="There was an error while loading your services"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="services.error.action" defaultMessage="Reload your services" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <ServicesTable services={services} />
        <ServicesPagination
          page={page}
          limit={LIMIT}
          totalItems={data?.servicesWithPagination?.total || 0}
          currentItemsCount={data?.servicesWithPagination?.items?.length || 0}
          setPage={setPage}
        />
      </>
    )
  }

  return (
    <>
      <ServicesFiltersWrapper>
        <ServicesFilters filters={filters} services={services} handleChange={handleFiltersChange} routeId={route.id} />
      </ServicesFiltersWrapper>
      <section>{content}</section>
      <Footer>
        {canWrite(FEATURES.SERVICE, login.accessToken.policies) && (
          <AddAction kind="secondary" as={NavLink} to={`/services/new?routeId=${route.id}`}>
            <PlusIcon />
            <FormattedMessage id="services.list.add" defaultMessage="Add Service" />
          </AddAction>
        )}
      </Footer>
    </>
  )
}
